import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Dropdown } from 'react-bootstrap';
import { Typography, IconButton, Button as MuiButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNotification } from '../../../../../hooks/NotificationContext';
import { getConfigurations } from '../../../../../modules/config';
import { fetchExecutionTestSteps, updateSingleScript, bulkUpdateExecutionStep } from '../../../../../modules/executions';
import TestStepItem from './TestStepItem';
import Modal from '../../../../elements/Modal'; // Use your custom Modal
import { isExecutiveManager } from '../../../../../lib/userUtils';
import Circle from '@mui/icons-material/Circle';
import IssueLinkManager from '../../../../elements/common/traceability/IssueLinkManager';

const TestStepsComponent = ({
  data,
  execution,
  error,
  fetchSelectedExecutionDetails,
  projectId,
  isTestCycleCompleted,
}) => {
  const { addNotification } = useNotification();
  const [testSteps, setTestSteps] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formData, setFormData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const defaultStatus = statuses?.find((status) => status?.is_default);
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const assignedTo = execution?.executed_by;
  const technologyLayer = execution?.association?.testcase?.technology_layer;
  const [openIssueLinkModal, setOpenIssueLinkModal] = useState(false);
  const [failedStep, setFailedStep] = useState(null);

  const handleStepFailed = (step) => {
    setFailedStep(step);
    setOpenIssueLinkModal(true);
  };

  const isAssignedToMe = assignedTo === currentUser?.id;
  const [selected, setSelected] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");

  const fetchStatuses = async () => {
    try {
      const [testStepsData, statusResponse] = await Promise.all([
        await fetchExecutionTestSteps(execution?.id),
        await getConfigurations('testexecutionstatus', projectId),
      ])

      if (testStepsData?.length > 0) {
        setTestSteps(testStepsData);
      } else {
        setTestSteps([]);
      }
      if (statusResponse) {
        setStatuses(statusResponse.data);
      }
    } catch (error) {
      console.error('Error fetching statuses:', error.message);
    }
  };

  useEffect(() => {
    if (execution && execution?.id) {
      fetchStatuses();
    }
  }, [execution]);

  useEffect(() => {
    const sortedTestSteps = [...testSteps].sort(
      (a, b) => a?.teststeps?.s_no - b?.teststeps?.s_no
    );

    const initialFormData = sortedTestSteps?.map((step) => ({
      ...step,
      uri: step?.uri,
      actualResult: step.actual_result || '',
      status: step.status_details ? step.status_details.id : defaultStatus?.id,
    }));
    setFormData(initialFormData);
  }, [execution, statuses, testSteps, defaultStatus]);

  const handleUpdateStep = async (values, id) => {
    setLoading(true);
    try {
      await updateSingleScript(id, { execution: values });
      addNotification('success', 'Test steps updated successfully!');
      fetchSelectedExecutionDetails(execution?.id);
    } catch (error) {
      if (error.data && error.data.error && error.data.error.refresh_required) {
        fetchSelectedExecutionDetails(execution?.id);
        addNotification('danger', error?.message || 'Failed to update test steps.');
      } else {
        addNotification('danger', 'Failed to update test steps.');
      }
    } finally {
      setLoading(false);
    }
  };
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setSelected([]);
    setSelectedRows([]);
    setBulkStatus("");
  }

  const handleRowSelect = (id, row) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) newSelected = selected.concat(id);
    else if (selectedIndex === 0) newSelected = selected.slice(1);
    else if (selectedIndex === selected.length - 1) newSelected = selected.slice(0, -1);
    else newSelected = selected.slice(0, selectedIndex).concat(selected.slice(selectedIndex + 1));

    setSelected(newSelected);
    setSelectedRows(formData.filter((r) => newSelected.includes(r.id)));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const newSelections = formData.map((r) => r.id);
      setSelected(newSelections);
      setSelectedRows(formData);
    } else {
      setSelected([]);
      setSelectedRows([]);
    }
  };
  const handleSaveClick = async () => {
    if (!bulkStatus || selectedRows.length === 0) return;

    setLoading(true);


    const payload = {
      executionId: execution?.id,
      statusId: bulkStatus,
      testStepIds: selectedRows.map((row) => row.id),
    };

    try {

      await bulkUpdateExecutionStep(payload);

      addNotification('success', 'Bulk update successful!');
      fetchSelectedExecutionDetails(execution?.id);
      setSelected([]);
      setSelectedRows([]);
      setBulkStatus("");
    } catch (error) {
      if (error.data && error.data.error && error.data.error.refresh_required) {
        fetchSelectedExecutionDetails(execution?.id);
        addNotification('danger', error?.message || 'Failed to update test steps.');
      } else {
        addNotification('danger', 'Failed to update test steps.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelected([]);
    setSelectedRows([]);
    setBulkStatus("");
  };

  const isExecutionEditable = execution?.is_editable && data?.status_details?.name === 'Approved' && isAssignedToMe && !isExecutiveManager();

  useEffect(() => {
    if (selected.length < 2) {
      setBulkStatus("");
    }
  }, [selected]);

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ height: '60px' }}>
          <h5 className="mb-0">Test Steps</h5>
        </Card.Header>
        <Card.Body className="text-center">
          {error && <p className="text-danger mb-3">{error}</p>}
          {testSteps?.length > 0 ? (
            <>
              <p className="mb-3">
                Click on the button below to review or begin executing the test
                steps.
              </p>
              <Button variant="primary" onClick={handleShowModal}>
                Execute
              </Button>
            </>
          ) : (
            <p className="text-center mb-0">No test steps found</p>
          )}
        </Card.Body>
      </Card>

      <Modal
        modalTitle="Execute Test Steps"
        open={showModal}
        handleClose={handleCloseModal}
        width="xl">
        <div style={{ overflowX: 'auto', minHeight: '350px' }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }} >
            {data.key} : {data.summary}
          </Typography>

          {isExecutionEditable && (
            <div className="d-flex flex-wrap mb-4">
              {formData.length > 1 && selected.length < 2 && (
                <div
                  className="w-100 p-2 mt-2"
                  style={{ borderLeft: '4px solid #6c757d', backgroundColor: '#f8f9fa' }}
                >
                  <p className="m-0" style={{ color: '#6c757d' }}>
                    Note: Select two or more test steps for bulk editing options.
                  </p>
                </div>
              )}

              {selected.length > 1 && (
                <div className="d-flex align-items-center gap-2 w-100 mt-2">
                  <Dropdown>
                    <Dropdown.Toggle
                      as="div"
                      role="button"
                      disabled={loading}
                      id={`status-dropdown`}
                      className="status-dropdown-toggle"
                      style={{
                        backgroundColor: bulkStatus ? statuses.find((s) => s.id === bulkStatus)?.color : '#1b3f87',
                        color: '#fff',
                        width: '160px'
                      }}>
                      {bulkStatus ? statuses.find((s) => s.id === bulkStatus)?.name : 'Select Status'}
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ width: '180px' }}>
                      {statuses.map((status) => (
                        <Dropdown.Item
                          key={status.id}
                          onClick={() => setBulkStatus(status.id)}
                          className="d-flex align-items-center"
                        >
                          <Circle
                            sx={{ color: status.color, fontSize: '1rem', mr: 1 }}
                          />
                          {status.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  <MuiButton variant="outlined" color="primary" onClick={handleSaveClick} disabled={loading || !bulkStatus}>
                    Save
                  </MuiButton>
                  <MuiButton variant="outlined" color="secondary" onClick={handleCancel}>
                    Cancel
                  </MuiButton>
                </div>
              )}
            </div>
          )}

          {selected.length > 0 && (
            <div className="d-flex justify-content-start align-items-center gap-3 mb-2">
              <Typography sx={{ color: '#1b3f87', marginBottom: 0 }}>
                {selected.length} records selected
              </Typography>
              <IconButton
                size="small"
                title="Clear Selection"
                onClick={handleCancel}
                sx={{
                  border: '1px solid',
                  borderColor: 'error.main',
                  color: 'error.main',
                  padding: '1px',
                }}
              >
                <CloseIcon fontSize="12px" />
              </IconButton>
            </div>
          )}
          <div style={{ minHeight: "300px", maxHeight: "500px", overflowY: "auto", overflowX: "auto" }}>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th className="text-center" style={{ width: '50px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={formData.length > 0 && selected.length > 0 && formData.every((r) => selected.includes(r.id))}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                {[
                  { label: 'Step', width: '3%' },
                  { label: 'Description', width: '20%' },
                  { label: 'Test Data', width: '15%' },
                  { label: 'Expected Result', width: '20%' },
                  ...(technologyLayer === 'API/Integration'
                    ? [{ label: 'URI', width: '10%' }]
                    : []),
                  { label: 'Actual Result', width: '20%' },
                  { label: 'Status', width: '12%' },
                  { label: '', width: '5%' },
                ].map((col) => (
                  <th
                    key={col.label}
                    style={{
                      width: col.width,
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                    }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formData?.map((script, index) => (
                <TestStepItem
                  key={script.id}
                  script={{ ...script, onStepFailed: () => handleStepFailed(script),  }}
                  index={index}
                  isTestCycleCompleted={isTestCycleCompleted}
                  statuses={statuses}
                  defaultStatus={defaultStatus}
                  isEditing={isExecutionEditable}
                  handleUpdateStep={handleUpdateStep}
                  loading={loading}
                  showUri={technologyLayer === 'API/Integration'}
                  isSelected={selected.includes(script.id)}
                  onSelectRow={(id) => handleRowSelect(id, script)}
                  isRowDisabled={(row) => !!row.isRowDisabled}
                />
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </Modal>
      <IssueLinkManager
        open={openIssueLinkModal}
        onClose={() => setOpenIssueLinkModal(false)}
        entityId={execution?.id}
        entityType="Execution"
        data={data}
        entityKey={data?.key}
        failedStep={failedStep}
      />
    </>
  );
};

export default TestStepsComponent;
